'use client';

import { H2Large } from 'components/home/styleConstants';
import { CustomImage as Image } from '..';
import { forwardRef, useRef, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import styles from './Contents.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

const Wrapper = forwardRef<
  HTMLDivElement,
  { backgroundColor?: string } & React.HTMLAttributes<HTMLDivElement>
>(({ backgroundColor, className, style, ...rest }, ref) => (
  <div
    ref={ref}
    className={cx(styles.wrapper, 'u-grid', className)}
    style={{ backgroundColor: backgroundColor || 'transparent', ...style }}
    {...rest}
  />
));
Wrapper.displayName = 'Wrapper';

// Hook to get translated default ring data
const useDefaultRingData = () => {
  const t = useTranslations('ring');
  return [
    {
      image: 'web_v2/illustrations/content-ring-air.png/w_800',
      title: t('ring.contents.data.Ring'),
    },
    {
      image: 'web_v2/illustrations/air-charger-only.png/w_800',
      title: t('ring.contents.data.Charger'),
    },
    {
      image: 'web_v2/illustrations/content-cable.png/w_800',
      title: t('ring.contents.data.Cable'),
    },
  ];
};

export interface ContentItem {
  image: string;
  title: ReactNode;
}

export interface ContentsProps {
  className?: string;
  htmlId?: string;
  contentData?: ContentItem[];
  backgroundColor?: string;
  contentsPadding?: string;
  $contentsBackgroundColor?: string;
  titleA?: ReactNode;
  titleB?: ReactNode;
}

export const Contents = ({
  className,
  htmlId,
  contentData,
  backgroundColor,
  contentsPadding,
  $contentsBackgroundColor,
  titleA,
  titleB,
}: ContentsProps) => {
  const t = useTranslations('ring');
  const tCommon = useTranslations('common');
  const rootRef = useRef<HTMLDivElement>(null);
  const defaultRingDataImages = useDefaultRingData();

  // Use provided data or default ring data for backward compatibility
  const data = contentData || defaultRingDataImages;
  const isMany = data.length > 3;

  // Use provided titles or default ring titles
  const displayTitleA = titleA || t('ring.contents.titleA');
  const displayTitleB = titleB || t('ring.contents.titleB');

  return (
    <Wrapper
      className={cx(styles.root, className)}
      id={htmlId}
      ref={rootRef}
      aria-labelledby="contents-heading"
      backgroundColor={backgroundColor}
      data-many={isMany ? 'true' : 'false'}
      style={{
        ['--contents-bg' as string]:
          $contentsBackgroundColor || 'rgba(238, 238, 238, 1)',
      }}
    >
      <div>
        <H2Large id="contents-heading" className={styles.title}>
          {displayTitleA} <span>{displayTitleB}</span>
        </H2Large>
        <ul
          className={cx(styles.contentsList, 'u-hide-scrollbar')}
          aria-label={tCommon('contents.content.ariaLabel.contentsBox')}
          style={{
            padding: contentsPadding,
          }}
        >
          {data.map((element, index) => {
            return (
              <li key={index} className={styles.boxContentElementWrapper}>
                <div
                  className={styles.boxContentElement}
                  role="img"
                  aria-label={`Image of ${
                    element.title as unknown as string
                  }`}
                >
                  <Image
                    src={element.image}
                    alt=""
                    width="200"
                    height="200"
                    aria-hidden="true"
                  />
                </div>
                <p>{element.title}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </Wrapper>
  );
};
